import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { FiShoppingCart, FiMinus, FiPlus, FiTrash2, FiArrowLeft, FiCheckCircle, FiLoader } from 'react-icons/fi'

export default function EcommercePage() {
  const { slug } = useParams()
  const navigate = useNavigate()

  const [website, setWebsite] = useState(null)
  const [products, setProducts] = useState([])
  const [cart, setCart] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCart, setShowCart] = useState(false)
  const [checkoutStep, setCheckoutStep] = useState('cart') // cart, shipping, review, payment
  const [processingPayment, setProcessingPayment] = useState(false)

  const [shippingInfo, setShippingInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: ''
  })

  useEffect(() => {
    fetchStoreData()
  }, [slug])

  const fetchStoreData = async () => {
    try {
      setLoading(true)
      const response = await axios.get(`/store-payments/store/${slug}/products`)
      setWebsite(response.data.website)
      setProducts(response.data.products)
    } catch (error) {
      console.error('Error fetching store:', error)
      safeToast(toast.error, 'Failed to load store')
    } finally {
      setLoading(false)
    }
  }

  const addToCart = (product) => {
    const existingItem = cart.find(item => item._id === product._id)

    if (existingItem) {
      setCart(cart.map(item =>
        item._id === product._id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ))
    } else {
      setCart([...cart, { ...product, quantity: 1 }])
    }

    safeToast(toast.success, `${product.name} added to cart`)
  }

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId)
    } else {
      setCart(cart.map(item =>
        item._id === productId
          ? { ...item, quantity }
          : item
      ))
    }
  }

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item._id !== productId))
  }

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0)
  }

  const safeToast = (fn, ...args) => {
    try {
      fn(...args)
    } catch (err) {
      console.error('Toast error:', err)
      if (args[0] && typeof args[0] === 'string') {
        alert(args[0])
      }
    }
  }

  const handleCheckoutSubmit = async (e) => {
    e.preventDefault()

    // Validate shipping info
    if (!shippingInfo.name || !shippingInfo.email || !shippingInfo.phone || !shippingInfo.address) {
      safeToast(toast.error, 'Please fill in all required fields')
      return
    }

    if (checkoutStep === 'shipping') {
      setCheckoutStep('review')
      return
    }

    if (checkoutStep === 'review') {
      // Keep the user on the review screen while payment is being processed
      initiatePayment()
    }
  }

  const initiatePayment = async () => {
    if (!website?.id) {
      safeToast(toast.error, 'Website not configured')
      return
    }

    console.log('[FRONTEND] Starting payment initiation:', {
      websiteId: website.id,
      websiteName: website.name,
      currency: website.currency,
      cartItems: cart.length,
      totalAmount: calculateTotal()
    })

    try {
      setProcessingPayment(true)

      const orderData = {
        items: cart.map(item => ({
          itemId: item._id,
          title: item.name,
          description: item.description,
          price: item.price,
          quantity: item.quantity,
          type: 'product'
        })),
        email: shippingInfo.email,
        name: shippingInfo.name,
        phone: shippingInfo.phone,
        amount: calculateTotal(),
        currency: website.currency,
        shipping: {
          address: shippingInfo.address,
          city: shippingInfo.city,
          state: shippingInfo.state,
          zipCode: shippingInfo.zipCode
        }
      }

      console.log('[FRONTEND] Sending checkout request:', orderData)

      const response = await axios.post(`/store-payments/store/${slug}/checkout`, orderData)

      console.log('[FRONTEND] Checkout response received:', response.data)

      if (response.data.success) {
        const { gateway, sessionId, publishableKey, redirectUrl } = response.data.data

        console.log('[FRONTEND] Payment data extracted:', {
          gateway,
          hasSessionId: !!sessionId,
          hasPublishableKey: !!publishableKey,
          hasRedirectUrl: !!redirectUrl,
          stripeAvailable: !!window.Stripe
        })

        if (gateway === 'stripe') {
          const stripeScriptReady = () => window.Stripe && typeof window.Stripe === 'function'

          if (!stripeScriptReady()) {
            console.warn('[FRONTEND] Stripe.js not loaded yet, injecting script...')
            const script = document.createElement('script')
            script.src = 'https://js.stripe.com/v3/'
            script.async = true
            document.head.appendChild(script)

            await new Promise((resolve, reject) => {
              script.onload = resolve
              script.onerror = () => reject(new Error('Failed to load Stripe.js'))
            })
          }

          if (sessionId && publishableKey && stripeScriptReady()) {
            console.log('[FRONTEND] Using Stripe SDK redirectToCheckout')
            try {
              // Handle Stripe payment with SDK
              const stripe = window.Stripe(publishableKey)
              const { error } = await stripe.redirectToCheckout({ sessionId })

              if (error) {
                console.error('Stripe redirect error:', error)
                try { toast.error(`Payment failed: ${error.message}`) } catch (_) { alert(`Payment failed: ${error.message}`) }
              }
            } catch (redirectError) {
              console.error('Stripe redirectToCheckout threw an error:', redirectError)
              try { toast.error('Unable to redirect to Stripe checkout') } catch (_) { alert('Unable to redirect to Stripe checkout') }
            }
          } else if (redirectUrl) {
            console.log('[FRONTEND] Falling back to Stripe URL redirect')
            safeToast(toast.success, 'Redirecting to Stripe checkout...')
            // Fallback: redirect to Stripe checkout URL
            window.location.href = redirectUrl
          } else {
            console.error('[FRONTEND] Stripe missing required data', response.data)
            try { toast.error('Failed to initialize Stripe payment (missing session URL or key)') } catch (_) { alert('Failed to initialize Stripe payment (missing session URL or key)') }
          }
        } else if (redirectUrl) {
          console.log('[FRONTEND] Using Paystack/other gateway redirect')
          safeToast(toast.success, 'Redirecting to payment provider...')
          // Handle Paystack or other gateways
          window.location.href = redirectUrl
        } else {
          console.error('[FRONTEND] No redirect URL available')
          safeToast(toast.error, 'Failed to initialize payment')
        }
      } else {
        const errorMessage = response.data.stripeError?.message || 
                           response.data.error || 
                           response.data.message || 
                           'Failed to initialize payment'
        console.error('[FRONTEND] Payment initialization failed:', response.data)
        toast.error(errorMessage)
      }
    } catch (error) {
      console.error('Error initiating payment:', error)
      toast.error(error.response?.data?.message || 'Failed to process payment')
    } finally {
      setProcessingPayment(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!website) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Store Not Found</h1>
          <button
            onClick={() => navigate('/')}
            className="mt-4 flex items-center gap-2 mx-auto px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <FiArrowLeft className="w-4 h-4" /> Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{website.name}</h1>
            <p className="text-sm text-gray-600">Online Store</p>
          </div>
          <button
            onClick={() => setShowCart(true)}
            className="relative flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <FiShoppingCart className="w-5 h-5" />
            {cart.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {cart.length}
              </span>
            )}
          </button>
        </div>
      </header>

      {showCart ? (
        // CART & CHECKOUT VIEW
        <div className="max-w-4xl mx-auto p-4">
          <button
            onClick={() => setShowCart(false)}
            className="flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-6"
          >
            <FiArrowLeft className="w-4 h-4" /> Continue Shopping
          </button>

          {checkoutStep === 'cart' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Shopping Cart</h2>

              {cart.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                  <FiShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">Your cart is empty</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  {cart.map(item => (
                    <div key={item._id} className="p-4 border-b border-gray-200 last:border-0 flex gap-4">
                      {item.image?.url && (
                        <img src={item.image.url} alt={item.name} className="w-20 h-20 object-cover rounded-lg" />
                      )}
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{item.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{item.currency} {item.price}</p>
                        <div className="flex items-center gap-2 mt-3">
                          <button
                            onClick={() => updateQuantity(item._id, item.quantity - 1)}
                            className="p-1 text-gray-500 hover:text-gray-900 border border-gray-300 rounded"
                          >
                            <FiMinus className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item._id, item.quantity + 1)}
                            className="p-1 text-gray-500 hover:text-gray-900 border border-gray-300 rounded"
                          >
                            <FiPlus className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeFromCart(item._id)}
                            className="ml-auto p-1 text-red-600 hover:text-red-700"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          {website.currency} {(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}

                  <div className="p-4 bg-gray-50 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-900">Total:</span>
                      <span className="text-2xl font-bold text-primary-600">
                        {website.currency} {calculateTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => setCheckoutStep('shipping')}
                    className="w-full p-4 bg-primary-600 text-white font-semibold hover:bg-primary-700 transition-colors"
                  >
                    Proceed to Checkout
                  </button>
                </div>
              )}
            </div>
          )}

          {checkoutStep === 'shipping' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Shipping Information</h2>

              <form onSubmit={handleCheckoutSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                  <input
                    type="text"
                    placeholder="Full Name *"
                    value={shippingInfo.name}
                    onChange={(e) => setShippingInfo({ ...shippingInfo, name: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    type="email"
                    placeholder="Email *"
                    value={shippingInfo.email}
                    onChange={(e) => setShippingInfo({ ...shippingInfo, email: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <input
                  type="tel"
                  placeholder="Phone Number *"
                  value={shippingInfo.phone}
                  onChange={(e) => setShippingInfo({ ...shippingInfo, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />

                <input
                  type="text"
                  placeholder="Street Address *"
                  value={shippingInfo.address}
                  onChange={(e) => setShippingInfo({ ...shippingInfo, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-4">
                  <input
                    type="text"
                    placeholder="City *"
                    value={shippingInfo.city}
                    onChange={(e) => setShippingInfo({ ...shippingInfo, city: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="State *"
                    value={shippingInfo.state}
                    onChange={(e) => setShippingInfo({ ...shippingInfo, state: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="ZIP Code *"
                    value={shippingInfo.zipCode}
                    onChange={(e) => setShippingInfo({ ...shippingInfo, zipCode: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full p-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Continue to Review
                </button>
              </form>
            </div>
          )}

          {checkoutStep === 'review' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Order Review</h2>

              <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Order Items</h3>
                  <div className="space-y-3">
                    {cart.map(item => (
                      <div key={item._id} className="flex justify-between">
                        <div>
                          <p className="text-gray-900">{item.name} x {item.quantity}</p>
                        </div>
                        <p className="text-gray-900 font-medium">
                          {website.currency} {(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Shipping To</h3>
                  <p className="text-sm text-gray-600">
                    {shippingInfo.name}<br />
                    {shippingInfo.address}<br />
                    {shippingInfo.city}, {shippingInfo.state} {shippingInfo.zipCode}<br />
                    {shippingInfo.phone}
                  </p>
                </div>

                <div className="border-t border-gray-200 pt-4 flex justify-between items-center text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-primary-600">{website.currency} {calculateTotal().toFixed(2)}</span>
                </div>

                <button
                  onClick={handleCheckoutSubmit}
                  disabled={processingPayment}
                  className="w-full p-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 disabled:bg-gray-400 transition-colors flex items-center justify-center gap-2"
                >
                  {processingPayment ? (
                    <>
                      <FiLoader className="animate-spin" /> Processing...
                    </>
                  ) : (
                    'Proceed to Payment'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        // PRODUCTS VIEW
        <div className="max-w-7xl mx-auto p-4">
          {products.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No products available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map(product => (
                <div key={product._id} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                  {product.image?.url && (
                    <img src={product.image.url} alt={product.name} className="w-full h-48 object-cover" />
                  )}
                  <div className="p-4 space-y-3">
                    <h3 className="font-semibold text-gray-900">{product.name}</h3>
                    {product.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">{product.description}</p>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold text-primary-600">
                        {website.currency} {product.price}
                      </span>
                      <button
                        onClick={() => addToCart(product)}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors"
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
